/**
 * MyCopro - Types Campagne de Validation Résidence
 * Permet au CS de lancer une campagne annuelle ou ponctuelle.
 * Les résidents doivent saisir un mot de passe partagé offline
 * pour prouver qu'ils habitent toujours dans la résidence.
 * Les non-validés sont déliés automatiquement à la clôture.
 *
 * Firestore:
 *   residences/{residenceId}/validationCampaigns/{campaignId}
 *   residences/{residenceId}/validationCampaigns/{campaignId}/responses/{userId}
 */

// ============================================================================
// STATUTS
// ============================================================================

export type CampaignStatus = 'active' | 'closed';

export type MemberValidationStatus = 'pending' | 'validated' | 'failed' | 'unlinked';

// ============================================================================
// CAMPAGNE DE VALIDATION
// ============================================================================

export interface ValidationCampaign {
  id: string;
  residenceId: string;

  /** Hash SHA-256 du mot de passe (normalisé: lowercase + trim) */
  codeHash: string;

  /** Mot de passe en clair — lisible par le CS pour communication offline */
  code?: string;

  /** Dates de la campagne */
  startDate: string;   // ISO
  endDate: string;     // ISO

  /** Statut courant */
  status: CampaignStatus;

  /** Nombre max de tentatives par résident */
  maxAttempts: number;

  /** Créateur de la campagne */
  createdBy: string;      // Firebase UID
  createdByName: string;

  /** Résultats (remplis à la clôture) */
  totalMembers?: number;
  validatedCount?: number;
  unlinkedCount?: number;

  /** Métadonnées */
  createdAt: string;   // ISO
  closedAt?: string;   // ISO
  closedBy?: string;   // Firebase UID
}

// ============================================================================
// RÉPONSE D'UN RÉSIDENT À LA CAMPAGNE
// ============================================================================

export interface CampaignResponse {
  id: string;                        // = userId
  userId: string;
  userName: string;
  userCategory: string;

  /** true si le résident a saisi le bon code */
  validated: boolean;

  /** Nombre de tentatives effectuées */
  attempts: number;

  /** Date de validation réussie */
  validatedAt?: string;  // ISO

  /** Date de la dernière tentative */
  lastAttemptAt?: string; // ISO
}

// ============================================================================
// FORMULAIRE DE CRÉATION
// ============================================================================

export interface CreateCampaignData {
  code: string;          // Mot de passe en clair (sera hashé avant stockage)
  startDate: string;     // ISO
  endDate: string;       // ISO
  maxAttempts?: number;  // Défaut: 5
}

// ============================================================================
// RÉSUMÉ CAMPAGNE (pour affichage)
// ============================================================================

export interface CampaignSummary {
  campaign: ValidationCampaign;
  responses: CampaignResponse[];
  validatedCount: number;
  pendingCount: number;
  failedCount: number;
  totalMembers: number;
  daysRemaining: number;
  progressPercent: number;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Hash un mot de passe avec SHA-256 (Web Crypto API)
 * Le mot de passe est normalisé: lowercase + trim
 */
export async function hashCampaignCode(code: string): Promise<string> {
  const normalized = code.toLowerCase().trim();
  const encoder = new TextEncoder();
  const data = encoder.encode(normalized);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Vérifie si un code correspond au hash stocké
 */
export async function verifyCampaignCode(code: string, storedHash: string): Promise<boolean> {
  const inputHash = await hashCampaignCode(code);
  return inputHash === storedHash;
}

/**
 * Calcule le nombre de jours restants
 */
export function getCampaignDaysRemaining(endDate: string): number {
  const end = new Date(endDate);
  const now = new Date();
  const diff = end.getTime() - now.getTime();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

/**
 * Vérifie si la campagne est encore dans sa période de collecte
 */
export function isCampaignInCollectionPeriod(campaign: ValidationCampaign): boolean {
  if (campaign.status !== 'active') return false;
  const now = new Date();
  const start = new Date(campaign.startDate);
  const end = new Date(campaign.endDate);
  return now >= start && now <= end;
}

/**
 * Vérifie si la date de fin de campagne est dépassée
 */
export function isCampaignExpired(campaign: ValidationCampaign): boolean {
  const now = new Date();
  const end = new Date(campaign.endDate);
  return now > end;
}
